'use client';

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import Quiz from '@/components/assessment/Quiz';
import { MarkdownAskAiToolbar } from '@/components/common/MarkdownAskAiToolbar';
import type { MarkdownExportSpec } from '@/lib/utils/markdown-export-path';
import useUserCourseWithLessons from '@/hooks/useUserCourseWithLessons';
import { Course } from '@/types/course';
import { fetchLearningPathLessons } from '@/lib/api/get-user-learning-paths';
import { getUserLessonActivity } from '@/lib/api/get-user-activity';

interface QuizClientWrapperProps {
  course: Course;
  courseId: string;
  lesson: any;
  learningPath?: string;
  learningPathData?: any;
  videoConfig?: any;
}

export default function QuizClientWrapper({
  course,
  courseId,
  lesson,
  learningPath,
  learningPathData,
  videoConfig,
}: QuizClientWrapperProps) {
  const courseUid = course?.uid;
  const lessonUid = lesson?.uid;

  // GET /v1/users/courses/:courseUid/lessons/:lessonUid – primary source for quiz revisit/refresh (score, attempts, show results and hide questionnaire)
  const { data: lessonWithProgress, isLoading: isProgressLoading } = useQuery({
    queryKey: ['user-lesson', courseUid, lessonUid],
    queryFn: async () => getUserLessonActivity(courseUid!, lessonUid!),
    enabled: !!courseUid && !!lessonUid,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const { userLessonData } = useUserCourseWithLessons({
    courseEntryUid: courseUid,
    lessons: course?.chapters?.[0]?.lessons?.lessons || [],
    lessonEntryUid: lessonUid,
  });

  const { data: learningPathLessonsData } = useQuery({
    queryKey: ['learning-path-lessons', courseUid],
    queryFn: () => fetchLearningPathLessons(course!.uid),
    enabled: !!learningPath && !!courseUid,
    staleTime: 0,
    refetchOnMount: true,
  });

  // Prefer single-lesson API progress so revisit/refresh shows quiz card (score, attempts) and hides questionnaire
  const lessonProgress = useMemo(() => {
    if (lessonWithProgress?.progress) return lessonWithProgress.progress;
    if (learningPathLessonsData?.lessons) {
      const userLesson = learningPathLessonsData.lessons.find(
        (ul: any) => ul.entryUid === lessonUid
      );
      return userLesson?.progress;
    }
    return userLessonData?.progress;
  }, [lessonWithProgress, learningPathLessonsData, userLessonData?.progress, lessonUid]);

  // Build assessment_test from lesson quiz data
  const assessment_test = useMemo(() => {
    if (!lesson?.quiz) return null;
    
    return {
      assessment: lesson.quiz.assessment || [],
      question_count: lesson.quiz.question_count || 0,
      passing_criteria: lesson.quiz.passing_criteria || 0,
    };
  }, [lesson?.quiz]);

  if (!assessment_test) {
    return null;
  }

  // Build assessment name from learning path or course
  const assessmentName = learningPathData?.title || course?.title || 'Quiz';

  const courseUrl = learningPath
    ? `/learning-paths/${learningPath}/${courseId}`
    : `/courses/${courseId}`;

  const seg = (s: string | undefined) =>
    (s ?? '').replace(/^\//, '').replace(/\/$/, '');
  const lessonSlug = seg(lesson?.url as string | undefined);
  const mdSpec: MarkdownExportSpec | null =
    lessonSlug && courseId
      ? learningPath
        ? {
            kind: 'learning_path_lesson',
            learningPath: seg(learningPath),
            courseId: seg(courseId),
            lessonId: lessonSlug,
          }
        : { kind: 'lesson', courseId: seg(courseId), lessonId: lessonSlug }
      : null;

  return (
    <div className="section bg-surface-2">
      <Quiz
        assessment_test={assessment_test}
        assessmentName={assessmentName}
        courseUid={courseUid}
        isStandaloneCourse={!learningPath}
        lessonUid={lessonUid}
        data={course}
        videoEntry={lesson}
        videoConfig={videoConfig}
        courseUrl={courseUrl}
        progress={lessonProgress}
        isProgressLoading={isProgressLoading}
        markdownToolbar={
          mdSpec ? <MarkdownAskAiToolbar spec={mdSpec} variant="inline" /> : undefined
        }
      />
    </div>
  );
}
